import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Fade,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Control, Controller, FieldErrors, useFieldArray, UseFormWatch } from 'react-hook-form';
import { Renter } from '../../utils/types/renter.interface';

interface Props {
  control: Control<Renter, any>;
  errors: FieldErrors<Renter>;
  watch: UseFormWatch<Renter>;
}

export const RenterContact = ({ control, errors, watch }: Props) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'applicantEmails',
  });

  const applicantEmails = watch('applicantEmails');

  const errorMsgForApplicantEmail = (index: number) => {
    const errorMsg = errors?.applicantEmails?.[index]?.value?.message;
    if (errorMsg) return errorMsg;
    const value = applicantEmails[index].value?.toLowerCase();
    const duplicatedError =
      value && applicantEmails.filter((email) => email.value?.toLowerCase() === value).length > 1
        ? 'Some renters seem to share the same email. Please check again.'
        : '';
    return duplicatedError ?? '';
  };

  return (
    <>
      <Typography variant="h5" component="h2" mb={1}>
        Renter contact information
      </Typography>
      <Typography variant="body1" mb={3}>
        Enter your renter's email address below. You can add up to five roommates, if applicable.
      </Typography>
      <Controller
        name="applicantEmails.0.value"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            ref={null}
            id="client-email-address"
            label="Renter email address"
            required
            error={!!errorMsgForApplicantEmail(0)}
            helperText={errorMsgForApplicantEmail(0) || ' '}
            InputProps={{
              classes: {
                root: 'fs-mask',
              },
            }}
          />
        )}
      />
      {fields.map(
        (roommate: { id: string }, index: number) =>
          index > 0 && (
            <Fade in={index > 0} key={roommate.id}>
              <Box>
                <Controller
                  name={`applicantEmails.${index}.value`}
                  key={roommate.id}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      ref={null}
                      id={`applicant-emails-${index}`}
                      label={`Roommate ${index} email address`}
                      required
                      error={!!errorMsgForApplicantEmail(index)}
                      helperText={errorMsgForApplicantEmail(index) || ' '}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="remove roommate"
                              onClick={() => remove(index)}
                              edge="end"
                            >
                              <Close />
                            </IconButton>
                          </InputAdornment>
                        ),
                        disableUnderline: true,
                        classes: {
                          root: 'fs-mask',
                        },
                      }}
                    />
                  )}
                />
              </Box>
            </Fade>
          ),
      )}
      {fields.length <= 5 && (
        <Button
          sx={{ mt: 1 }}
          variant="outlined"
          onClick={() => append({ value: '' })}
          id="add-roommate-button"
        >
          Add Roommate
        </Button>
      )}
    </>
  );
};
